import React, { useEffect, useMemo, useState } from 'react'
import { Button, Form, Table } from 'semantic-ui-react'
import { useForm } from 'react-hook-form'
import {
    AppPrimaryButton,
    FormCheckBoxField,
    FormDropdownField,
    FormTextFormField,
} from 'elements'
import {
    AuthService,
    InspectionTaskService,
    buildMonthOptions,
    buildWeekOfMonthDropdownOptions,
    buildYearOptions,
    defaultMonthWeekTitle,
    formatWeekLabel,
    isoWeekAndYearFromDate,
    localDateToIso,
} from 'utility'
import { useNotification } from 'hooks'
import {
    RotationStepBody,
    RotationStepCard,
    RotationStepHeader,
    RotationStepSubtitle,
    RotationStepTitle,
} from './rotation-section-styles'

const WK_COMPANY_ID = 34930
const DEFAULT_CUSTOMER_ID_34930 = 23

const toTemplateIdsString = ids => {
    if (!ids) return ''
    const list = Array.isArray(ids) ? ids : [ids]
    return list.filter(Boolean).join(';')
}

const getCompanyId = () => {
    const user = AuthService.getUser()
    return user?.Companies?.[0]?.CompanyId || user?.CompanyId
}

const ScheduleByWeekSectionLegacy = ({ employeeId, templateId }) => {
    const { errorToast, successToast } = useNotification()
    const companyId = getCompanyId()

    const now = new Date()

    const [enabled, setEnabled] = useState(false)
    const [weekYear, setWeekYear] = useState(now.getFullYear())
    const [weekMonth, setWeekMonth] = useState(now.getMonth() + 1)
    const [weekOfMonth, setWeekOfMonth] = useState(1)
    const [queuedWeeks, setQueuedWeeks] = useState([])
    const [queueErrors, setQueueErrors] = useState({})
    const [submitting, setSubmitting] = useState(false)
    const [sendEmailReminder, setSendEmailReminder] = useState(true)
    const [sendOnlyOnce, setSendOnlyOnce] = useState(false)

    useEffect(() => {
        InspectionTaskService.isScheduleByWeekEnabled()
            .then(res => setEnabled(res?.Enabled === true))
            .catch(() => setEnabled(false))
    }, [])

    const monthOptions = useMemo(() => buildMonthOptions(), [])
    const yearOptions = useMemo(() => buildYearOptions(), [])

    const weekDropdownOptions = useMemo(
        () => buildWeekOfMonthDropdownOptions(weekYear, weekMonth),
        [weekYear, weekMonth],
    )

    const selectedWeek = useMemo(
        () =>
            weekDropdownOptions.find(option => option.value === weekOfMonth) ||
            weekDropdownOptions[0] ||
            null,
        [weekDropdownOptions, weekOfMonth],
    )

    useEffect(() => {
        if (weekDropdownOptions.length === 0) return
        if (!weekDropdownOptions.some(option => option.value === weekOfMonth)) {
            setWeekOfMonth(weekDropdownOptions[0].value)
        }
    }, [weekDropdownOptions, weekOfMonth])

    const weekTitle = selectedWeek
        ? defaultMonthWeekTitle(weekMonth, weekYear, weekOfMonth)
        : ''

    const handleYearChange = (_, { value }) => {
        setWeekYear(parseInt(value, 10))
        setWeekOfMonth(1)
    }

    const handleMonthChange = (_, { value }) => {
        setWeekMonth(parseInt(value, 10))
        setWeekOfMonth(1)
    }

    const handleAddToQueue = () => {
        if (!employeeId || !templateId) {
            errorToast('Select Auditor and Template before adding a week.')
            return
        }
        if (!selectedWeek) {
            errorToast('Select a valid week.')
            return
        }

        const key = `${weekYear}-${weekMonth}-${weekOfMonth}`
        if (queuedWeeks.some(row => row.key === key)) return

        const wednesday = new Date(selectedWeek.sunday)
        wednesday.setDate(wednesday.getDate() + 3)
        const { year: isoYear, week: isoWeek } = isoWeekAndYearFromDate(wednesday)

        setQueuedWeeks(prev => [
            ...prev,
            {
                key,
                weekOfMonth,
                month: weekMonth,
                year: weekYear,
                isoWeek,
                isoYear,
                title: weekTitle,
                startDate: localDateToIso(selectedWeek.sunday),
                endDate: localDateToIso(selectedWeek.saturday),
            },
        ])
        setQueueErrors(prev => {
            const next = { ...prev }
            delete next[key]
            return next
        })
    }

    const removeQueuedWeek = key => {
        setQueuedWeeks(prev => prev.filter(row => row.key !== key))
        setQueueErrors(prev => {
            const next = { ...prev }
            delete next[key]
            return next
        })
    }

    const handleScheduleWeeks = async () => {
        if (!employeeId || !templateId) {
            errorToast('Select Auditor and Template before scheduling.')
            return
        }
        if (queuedWeeks.length === 0) {
            errorToast('Add at least one week.')
            return
        }

        const customerId =
            companyId === WK_COMPANY_ID ? DEFAULT_CUSTOMER_ID_34930 : null
        if (!customerId) {
            errorToast('Customer is not configured for week scheduling.')
            return
        }

        setSubmitting(true)
        const errors = {}
        let successCount = 0

        await queuedWeeks.reduce(async (prev, row) => {
            await prev
            try {
                await InspectionTaskService.scheduleByWeek({
                    Title: row.title,
                    CustomerIds: String(customerId),
                    TeamMemberIds: String(employeeId),
                    TemplateIds: String(templateId),
                    WeekNumber: row.isoWeek,
                    Year: row.isoYear,
                    SendEmailReminder: sendEmailReminder,
                    SendOnlyOnce: sendOnlyOnce,
                })
                successCount += 1
            } catch (error) {
                const apiError = error?.error
                errors[row.key] =
                    apiError?.Message ||
                    apiError?.message ||
                    (typeof apiError === 'string' ? apiError : null) ||
                    'Failed to schedule this week.'
            }
        }, Promise.resolve())

        setSubmitting(false)
        setQueueErrors(errors)

        if (successCount > 0) {
            successToast(
                `Scheduled ${successCount} week${successCount === 1 ? '' : 's'} successfully.`,
            )
            if (Object.keys(errors).length === 0) {
                setQueuedWeeks([])
            }
        }
    }

    if (!enabled) return null

    return (
        <div className="my-3">
            <h5 style={{ marginBottom: '12px' }}>WK Audit Schedule</h5>
            <Form.Group widths="equal">
                <Form.Field>
                    <label>Month</label>
                    <Form.Select
                        options={monthOptions}
                        value={weekMonth}
                        onChange={handleMonthChange}
                    />
                </Form.Field>
                <Form.Field>
                    <label>Year</label>
                    <Form.Select
                        options={yearOptions}
                        value={weekYear}
                        onChange={handleYearChange}
                    />
                </Form.Field>
                <Form.Field>
                    <label>Week of Month</label>
                    <Form.Select
                        options={weekDropdownOptions}
                        value={weekOfMonth}
                        onChange={(_, { value }) =>
                            setWeekOfMonth(parseInt(value, 10))
                        }
                        disabled={weekDropdownOptions.length === 0}
                    />
                </Form.Field>
            </Form.Group>
            {selectedWeek && (
                <Form.Group widths="equal">
                    <Form.Field>
                        <label>Start (Sun)</label>
                        <Form.Input
                            readOnly
                            value={`${localDateToIso(selectedWeek.sunday)} (${formatWeekLabel(selectedWeek.sunday).split(',')[0]})`}
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>End (Sat)</label>
                        <Form.Input
                            readOnly
                            value={`${localDateToIso(selectedWeek.saturday)} (${formatWeekLabel(selectedWeek.saturday).split(',')[0]})`}
                        />
                    </Form.Field>
                </Form.Group>
            )}
            <div className="mb-3">
                <AppPrimaryButton type="button" onClick={handleAddToQueue}>
                    Add New
                </AppPrimaryButton>
            </div>
            {queuedWeeks.length > 0 && (
                <Form.Field>
                    <label>Queued weeks</label>
                    <Table celled compact>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>Week</Table.HeaderCell>
                                <Table.HeaderCell>Title</Table.HeaderCell>
                                <Table.HeaderCell>Start (Sun)</Table.HeaderCell>
                                <Table.HeaderCell>End (Sat)</Table.HeaderCell>
                                <Table.HeaderCell />
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {queuedWeeks.map(row => (
                                <Table.Row
                                    key={row.key}
                                    error={!!queueErrors[row.key]}
                                >
                                    <Table.Cell>Week {row.weekOfMonth}</Table.Cell>
                                    <Table.Cell>
                                        {row.title}
                                        {queueErrors[row.key] && (
                                            <div className="error">
                                                {queueErrors[row.key]}
                                            </div>
                                        )}
                                    </Table.Cell>
                                    <Table.Cell>{row.startDate}</Table.Cell>
                                    <Table.Cell>{row.endDate}</Table.Cell>
                                    <Table.Cell textAlign="center">
                                        <Button
                                            type="button"
                                            icon="close"
                                            size="mini"
                                            color="red"
                                            basic
                                            onClick={() => removeQueuedWeek(row.key)}
                                        />
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                </Form.Field>
            )}
            <Form.Group widths="equal">
                <Form.Field>
                    <Form.Checkbox
                        label="Send Email Reminder"
                        checked={sendEmailReminder}
                        onChange={(_, { checked }) =>
                            setSendEmailReminder(checked)
                        }
                    />
                </Form.Field>
                {sendEmailReminder && (
                    <Form.Field>
                        <Form.Checkbox
                            label="Send Only once"
                            checked={sendOnlyOnce}
                            onChange={(_, { checked }) =>
                                setSendOnlyOnce(checked)
                            }
                        />
                    </Form.Field>
                )}
            </Form.Group>
            <div className="mb-3">
                <AppPrimaryButton
                    type="button"
                    loading={submitting}
                    onClick={handleScheduleWeeks}
                    disabled={queuedWeeks.length === 0}
                >
                    {`Submit (${queuedWeeks.length} week${
                        queuedWeeks.length === 1 ? '' : 's'
                    })`}
                </AppPrimaryButton>
            </div>
        </div>
    )
}

const ScheduleByWeekSectionWk = ({
    control,
    errors,
    employeeId,
    templateIds,
    weekScheduleTitle,
    templates,
    templateLoading,
    scheduleMonth,
    scheduleYear,
}) => {
    const { errorToast, successToast } = useNotification()
    const companyId = getCompanyId()

    const [enabled, setEnabled] = useState(false)
    const [queuedWeeks, setQueuedWeeks] = useState([])
    const [queueErrors, setQueueErrors] = useState({})
    const [submitting, setSubmitting] = useState(false)

    const {
        control: weekControl,
        watch,
        setValue,
    } = useForm({
        defaultValues: {
            weekOfMonth: 1,
            sendEmailReminder: true,
            sendOnlyOnce: false,
        },
    })

    const weekOfMonth = watch('weekOfMonth')
    const sendEmailReminder = watch('sendEmailReminder')
    const sendOnlyOnce = watch('sendOnlyOnce')

    let selectedTemplateCount = 0
    if (Array.isArray(templateIds)) {
        selectedTemplateCount = templateIds.filter(Boolean).length
    } else if (templateIds) {
        selectedTemplateCount = 1
    }

    useEffect(() => {
        InspectionTaskService.isScheduleByWeekEnabled()
            .then(res => setEnabled(res?.Enabled === true))
            .catch(() => setEnabled(false))
    }, [])

    const weekDropdownOptions = useMemo(
        () =>
            scheduleMonth && scheduleYear
                ? buildWeekOfMonthDropdownOptions(scheduleYear, scheduleMonth)
                : [],
        [scheduleYear, scheduleMonth],
    )

    const selectedWeek = useMemo(
        () =>
            weekDropdownOptions.find(option => option.value === weekOfMonth) ||
            weekDropdownOptions[0] ||
            null,
        [weekDropdownOptions, weekOfMonth],
    )

    useEffect(() => {
        setValue('weekOfMonth', 1)
    }, [scheduleMonth, scheduleYear, setValue])

    useEffect(() => {
        if (weekDropdownOptions.length === 0) return
        if (!weekDropdownOptions.some(option => option.value === weekOfMonth)) {
            setValue('weekOfMonth', weekDropdownOptions[0].value)
        }
    }, [weekDropdownOptions, weekOfMonth, setValue])

    const weekTitle =
        selectedWeek && scheduleMonth && scheduleYear
            ? weekScheduleTitle?.trim() ||
              defaultMonthWeekTitle(scheduleMonth, scheduleYear, weekOfMonth)
            : ''

    const handleAddToQueue = () => {
        if (!employeeId) {
            errorToast('Select Auditor before adding a week.')
            return
        }
        if (!selectedTemplateCount) {
            errorToast('Select at least one Template before adding a week.')
            return
        }
        if (!scheduleMonth || !scheduleYear) {
            errorToast('Select month and year in Monthly Rotation section first.')
            return
        }
        if (!selectedWeek) {
            errorToast('Select a valid week.')
            return
        }

        const key = `${scheduleYear}-${scheduleMonth}-${weekOfMonth}`
        if (queuedWeeks.some(row => row.key === key)) return

        const wednesday = new Date(selectedWeek.sunday)
        wednesday.setDate(wednesday.getDate() + 3)
        const { year: isoYear, week: isoWeek } = isoWeekAndYearFromDate(wednesday)

        setQueuedWeeks(prev => [
            ...prev,
            {
                key,
                weekOfMonth,
                month: scheduleMonth,
                year: scheduleYear,
                isoWeek,
                isoYear,
                title: weekTitle,
                startDate: localDateToIso(selectedWeek.sunday),
                endDate: localDateToIso(selectedWeek.saturday),
            },
        ])
        setQueueErrors(prev => {
            const next = { ...prev }
            delete next[key]
            return next
        })
    }

    const removeQueuedWeek = key => {
        setQueuedWeeks(prev => prev.filter(row => row.key !== key))
        setQueueErrors(prev => {
            const next = { ...prev }
            delete next[key]
            return next
        })
    }

    const handleScheduleWeeks = async () => {
        if (!employeeId) {
            errorToast('Select Auditor before scheduling.')
            return
        }
        if (!selectedTemplateCount) {
            errorToast('Select at least one Template before scheduling.')
            return
        }
        if (queuedWeeks.length === 0) {
            errorToast('Add at least one week.')
            return
        }

        const customerId =
            companyId === WK_COMPANY_ID ? DEFAULT_CUSTOMER_ID_34930 : null
        if (!customerId) {
            errorToast('Customer is not configured for week scheduling.')
            return
        }

        const templateIdsString = toTemplateIdsString(templateIds)

        setSubmitting(true)
        const errorsMap = {}
        let successCount = 0

        await queuedWeeks.reduce(async (prev, row) => {
            await prev
            try {
                await InspectionTaskService.scheduleByWeek({
                    Title: row.title,
                    CustomerIds: String(customerId),
                    TeamMemberIds: String(employeeId),
                    TemplateIds: templateIdsString,
                    WeekNumber: row.isoWeek,
                    Year: row.isoYear,
                    SendEmailReminder: sendEmailReminder,
                    SendOnlyOnce: sendOnlyOnce,
                })
                successCount += 1
            } catch (error) {
                const apiError = error?.error
                errorsMap[row.key] =
                    apiError?.Message ||
                    apiError?.message ||
                    (typeof apiError === 'string' ? apiError : null) ||
                    'Failed to schedule this week.'
            }
        }, Promise.resolve())

        setSubmitting(false)
        setQueueErrors(errorsMap)

        if (successCount > 0) {
            successToast(
                `Scheduled ${successCount} week${successCount === 1 ? '' : 's'} successfully.`,
            )
            if (Object.keys(errorsMap).length === 0) {
                setQueuedWeeks([])
            }
        }
    }

    if (!enabled) return null

    return (
        <RotationStepCard>
            <RotationStepHeader>
                <RotationStepTitle>WK Audit Schedule</RotationStepTitle>
                <RotationStepSubtitle>
                    Select templates and weekly periods to schedule audits for the
                    chosen month and year above.
                </RotationStepSubtitle>
            </RotationStepHeader>
            <RotationStepBody>
                <>
                    <Form.Group widths="equal">
                        <FormTextFormField
                            control={control}
                            label="Title"
                            name="WeekScheduleTitle"
                            placeholder="Enter schedule title"
                            errors={errors?.WeekScheduleTitle}
                        />
                    </Form.Group>
                    <Form.Group widths="equal">
                        <FormDropdownField
                            control={control}
                            label="Templates"
                            name="WeekTemplateIds"
                            placeholder="Select templates"
                            errors={errors?.WeekTemplateIds}
                            options={templates}
                            loading={templateLoading}
                            search
                            multiple
                            floating
                        />
                        <FormDropdownField
                            control={weekControl}
                            label="Week of Month"
                            name="weekOfMonth"
                            placeholder={
                                scheduleMonth && scheduleYear
                                    ? 'Select week'
                                    : 'Select month & year above first'
                            }
                            options={weekDropdownOptions}
                            disabled={
                                !scheduleMonth ||
                                !scheduleYear ||
                                weekDropdownOptions.length === 0
                            }
                            floating
                        />
                    </Form.Group>
                    <div className="mb-3">
                        <AppPrimaryButton type="button" onClick={handleAddToQueue}>
                            Add New
                        </AppPrimaryButton>
                    </div>
                    {queuedWeeks.length > 0 && (
                        <Form.Field>
                            <label>Queued weeks</label>
                            <Table celled compact>
                                <Table.Header>
                                    <Table.Row>
                                        <Table.HeaderCell>Week</Table.HeaderCell>
                                        <Table.HeaderCell>Title</Table.HeaderCell>
                                        <Table.HeaderCell>Start (Sun)</Table.HeaderCell>
                                        <Table.HeaderCell>End (Sat)</Table.HeaderCell>
                                        <Table.HeaderCell />
                                    </Table.Row>
                                </Table.Header>
                                <Table.Body>
                                    {queuedWeeks.map(row => (
                                        <Table.Row
                                            key={row.key}
                                            error={!!queueErrors[row.key]}
                                        >
                                            <Table.Cell>Week {row.weekOfMonth}</Table.Cell>
                                            <Table.Cell>
                                                {row.title}
                                                {queueErrors[row.key] && (
                                                    <div className="error">
                                                        {queueErrors[row.key]}
                                                    </div>
                                                )}
                                            </Table.Cell>
                                            <Table.Cell>{row.startDate}</Table.Cell>
                                            <Table.Cell>{row.endDate}</Table.Cell>
                                            <Table.Cell textAlign="center">
                                                <Button
                                                    type="button"
                                                    icon="close"
                                                    size="mini"
                                                    color="red"
                                                    basic
                                                    onClick={() =>
                                                        removeQueuedWeek(row.key)
                                                    }
                                                />
                                            </Table.Cell>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table>
                        </Form.Field>
                    )}
                    <Form.Group widths="equal">
                        <FormCheckBoxField
                            control={weekControl}
                            name="sendEmailReminder"
                            label="Send Email Reminder"
                        />
                        {sendEmailReminder && (
                            <FormCheckBoxField
                                control={weekControl}
                                name="sendOnlyOnce"
                                label="Send Only once"
                            />
                        )}
                    </Form.Group>
                    <div className="mb-0">
                        <AppPrimaryButton
                            type="button"
                            loading={submitting}
                            onClick={handleScheduleWeeks}
                            disabled={queuedWeeks.length === 0}
                        >
                            {`Submit (${queuedWeeks.length} week${
                                queuedWeeks.length === 1 ? '' : 's'
                            })`}
                        </AppPrimaryButton>
                    </div>
                </>
            </RotationStepBody>
        </RotationStepCard>
    )
}

export const ScheduleByWeekSection = props => {
    const isWkCompany = Number(getCompanyId()) === WK_COMPANY_ID

    if (!isWkCompany) {
        return (
            <ScheduleByWeekSectionLegacy
                employeeId={props.employeeId}
                templateId={props.templateId}
            />
        )
    }

    return <ScheduleByWeekSectionWk {...props} />
}
